import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import { useIntl } from "react-intl";
import { makeAnAppointment, rescheduleAnAppointment } from "../../../../../api/appointments";
import SuccessDialog from "../dialogs/successDialog";
import ErrorDialog from "../../../../../components/dialogs/ErrorDialog";
import { AppointmentCreatedByEnum } from "../../../../../enums/appointmentCreatedBy";
import { IAppointmentForm, useSchedulingFormSummary } from "../../../../../hooks/useSchedulingForm";
import { getAssetRioPlan } from "../../../../../services/assetService";
import VehicleIcon from "../../../../../components/VehicleIcon";

interface IAppointmentSummaryProps {
  appointmentForm?: IAppointmentForm;
  setStep: (step: number) => void;
  schedulingId: string | null;
}

const AppointmentSummary = (props: IAppointmentSummaryProps) => {
  const {
    userProfile,
    loading, setLoading,
    acceptedTerms, setAcceptedTerms,
    successDialogOpen, setSuccessDialogOpen,
    errorDialog, setErrorDialog,
  } = useSchedulingFormSummary();

  const { plans } = getAssetRioPlan(props.appointmentForm?.assetsAndDrivers?.asset?.id ?? '');
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `intl-msg:appointment.step4.${id}` });

  return (
    <div className="scheduling-page resume-section p-10">
      <h2 className="text-size-h2 text-center text-color-black font-semibold padding-top-20 padding-bottom-20">
        {t('title')}
      </h2>
      <div className="resume-container">
        <h5 className="text-size-20 text-color-black font-bold margin-bottom-20 text-color-gray">
          {props.appointmentForm?.scheduleNumber}
        </h5>

        <div className="row schedule-data">
          <div className="col-4">
            <label className="block text-sm text-size-20 text-color-black font-bold margin-bottom-10">
              {t('driver')}
            </label>
            <div className="mt-1 p-2 border schedule-data__info">
              <div className="truncate">
                {props.appointmentForm?.assetsAndDrivers?.driver?.name}
              </div>
            </div>
          </div>
          <div className="col-2">
            <label className="block text-sm text-size-20 text-color-black font-bold margin-bottom-10">
              {t('license')}
            </label>
            <div className="mt-1 p-2 border schedule-data__info">
              <div className="truncate">
                {props.appointmentForm?.assetsAndDrivers?.driver?.license}
              </div>
            </div>
          </div>
          <div className="col-3">
            <label className="block text-sm text-size-20 text-color-black font-bold margin-bottom-10">
              {t('phone')}
            </label>
            <div className="mt-1 p-2 border schedule-data__info">
              <div className="truncate">
                {props.appointmentForm?.assetsAndDrivers?.driver?.phone}
              </div>
            </div>
          </div>
          <div className="col-3">
            <label className="block text-sm text-size-20 text-color-black font-bold margin-bottom-10">
              {t('email')}
            </label>
            <div className="mt-1 p-2 border schedule-data__info">
              <div className="truncate">
                {props.appointmentForm?.assetsAndDrivers?.driver?.email}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-lighter margin-top-20 vehicle-detais">
          <div className="flex items-start gap-4">
            <VehicleIcon 
              chassis={props.appointmentForm?.assetsAndDrivers?.asset?.chassis} 
              size={80}
            />

            <div className="flex-1 padding-left-10">
              <p className="text-color-black text-size-16 font-medium m-0">
                {props.appointmentForm?.assetsAndDrivers?.asset?.name} | {t('plateLabel')}{" "}
                {props.appointmentForm?.assetsAndDrivers?.asset?.licensePlate}
              </p>
              <p className="text-color-status-resting text-size-14 m-0">
                {t('chassisLabel')} {props.appointmentForm?.assetsAndDrivers?.asset?.chassis}
                <br />
                {t('odometerLabel')} {
                  props.appointmentForm?.assetsAndDrivers?.asset?.odometer
                }{" "}
                km | {t('hourmeterLabel')}{" "}
                {props.appointmentForm?.assetsAndDrivers?.asset?.hourmeter}
              </p>
            </div>

            {(props.appointmentForm?.assetsAndDrivers?.asset?.plans?.length ?? 0) >
              0 && (
                <div className="flex gap-6 margin-bottom-20">
                  <span className="px-2 py-1 green-tag">RIO</span>
                  <span className="px-2 py-1 green-tag">{t('inWarranty')}</span>
                  {props.appointmentForm?.assetsAndDrivers?.asset?.volksTotalPlan && (
                    <span className="px-2 py-1 bg-white dark:bg-neutral-800 white-tag">
                      Volkstotal{" "}
                      {props.appointmentForm?.assetsAndDrivers?.asset?.volksTotalPlan}
                    </span>
                  )}
                </div>
              )}
          </div>

          <div className="padding-top-10">
            <div className="car-workshop">
              <ul className="flex flex-column gap-5 m-0">
                {
                  (props.appointmentForm?.assetsAndDrivers?.schedulingTypes ?? []).includes('workshopActions') &&
                  <li className="flex justify-between">
                    <div className="text-color-black">{t('fieldCampaigns')}</div>
                    <div className="text-color-black font-bold">{props.appointmentForm?.assetsAndDrivers?.asset?.id ? plans : t('free')}</div>
                  </li>
                }

                <li className="flex justify-between">
                  <div className="text-color-black">
                    {/* {appointmentForm?.assetsAndDrivers?.checkup?.description} */}
                    {t('revision')}
                  </div>
                  <div className="text-color-black font-bold">{props.appointmentForm?.assetsAndDrivers?.asset?.id ? plans : t('free')}</div>
                </li>

                <li className="flex justify-between">
                  <div className="text-color-black">{t('totalServices')}</div>
                  <div className="text-color-black">
                    {(
                      props.appointmentForm?.assetsAndDrivers?.aditionalServices ?? []
                    )
                      .reduce((acc, service) => acc + service.price, 0)
                      .toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="revision-itens">
          <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
            {t('coveredItems')}
          </h5>
          <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              {
                props.appointmentForm?.assetsAndDrivers?.checkup?.services.length === 0 && 
                (props.appointmentForm?.assetsAndDrivers?.checkup?.parts ?? []).length === 0 &&
                <p>-</p>
              }
              {props.appointmentForm?.assetsAndDrivers?.checkup?.services.map(
                (item, index) => (
                  <div key={index} className="padding-bottom-10">
                    <p className="text-bold">{item.name}</p>
                    {item.items.map((subItem, subIndex) => (
                      <p key={subIndex} className="padding-left-20">
                        {subItem}
                      </p>
                    ))}
                  </div>
                )
              )}

              {props.appointmentForm?.assetsAndDrivers?.checkup?.aditionalServices?.length ?
                <div className="padding-bottom-10">
                  <p className="text-bold">{t('complementaryServices')}</p>
                  {props.appointmentForm?.assetsAndDrivers?.checkup?.aditionalServices.map(
                    (item, index) => (
                      <p key={index} className="padding-left-20">
                        {item.name}
                      </p>
                    )
                  )}
                </div> : null}

              {props.appointmentForm?.assetsAndDrivers?.checkup?.parts?.length && 
                !(props.appointmentForm?.assetsAndDrivers?.schedulingTypes ?? []).includes('corrective') ?
                <div className="padding-bottom-10">
                  <p className="text-bold">{t('parts')}</p>
                  {props.appointmentForm?.assetsAndDrivers?.checkup?.parts.map(
                    (item, index) => (
                      <p key={index} className="padding-left-20 py-2">
                        {item.qtd}x Cód.: {item.code} - {item.description}
                      </p>
                    )
                  )}
                </div> : null}

              {(props.appointmentForm?.assetsAndDrivers?.schedulingTypes ?? []).includes('workshopActions') &&
                <div className="padding-bottom-10">
                  <p className="text-bold">{t('fieldCampaignsSection')}</p>
                  {
                    (props.appointmentForm?.assetsAndDrivers?.asset?.campaigns?.list ?? [])
                      .map((subItem, subIndex) => (
                        <p key={subIndex} className="padding-left-20">
                          {subItem.name}
                        </p>
                      ))}
                </div>}
            </ul>
          </div>
        </div>

        <div className="revision-itens">
          <h5 className="text-size-20 text-bold margin-bottom-0">
            {t('contractedServicesTitle')}
          </h5>
          <div className="text-size-14 margin-bottom-10">
            {t('contractedServicesDesc')}
          </div>
          <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              {props.appointmentForm?.assetsAndDrivers?.aditionalServices?.map(
                (item, index) => (
                  <div key={index}>
                    <p>{item.name}</p>
                  </div>
                )
              )}
              {(props.appointmentForm?.assetsAndDrivers?.aditionalServices ?? []).length === 0 && <p>-</p>}
            </ul>
          </div>
        </div>

        <div className="revision-itens observation">
          <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
            {t('observations')}
          </h5>
          <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              <li className="flex justify-between">
                <span>{props.appointmentForm?.assetsAndDrivers?.obs || '-'}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="revision-itens flex justify-between gap-6">
          <div className="w-1/3">
            <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
              {t('dealershipTitle')}
            </h5>
            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700 card">
              <p className="m-0 text-size-h5">
                {props.appointmentForm?.dealership?.name}
              </p>
              <p className="m-0">{props.appointmentForm?.dealership?.address}</p>
            </div>
          </div>

          <div className="w-1/3">
            <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
              {t('dateAndTime')}
            </h5>
            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700 card">
              <p className="m-0 text-size-h5">
                {props.appointmentForm?.dateAndConsultant?.scheduleDate?.toLocaleDateString(
                  "pt-BR",
                  { year: "numeric", month: "long", day: "numeric" }
                )}
              </p>
              <p className="m-0">
                {props.appointmentForm?.dateAndConsultant?.scheduleDate?.toLocaleTimeString(
                  "pt-BR",
                  { hour: "2-digit", minute: "2-digit" }
                )}
              </p>
            </div>
          </div>

          <div className="w-1/3">
            <h5 className="text-size-20 text-color-black font-bold margin-bottom-10">
              {t('consultant')}
            </h5>
            <div className="p-4 bg-white dark:bg-neutral-800 border dark:border-neutral-700 card">
              <p className="m-0 text-size-h5">
                {props.appointmentForm?.dateAndConsultant?.consultant?.name}
              </p>
            </div>
          </div>
        </div>
        
        <div className="warning-row">
          <h5 className="text-color-status-resting">{t('attention')}</h5>
          <p className="text-color-warning text-size-18 font-semibold">
            {t('warrantyWarning')}
          </p>
          <p className="margin-top-20">
            <Checkbox
              checked={acceptedTerms}
              onChange={() => setAcceptedTerms(!acceptedTerms)}
            >
              {t('termsCheckbox')}
            </Checkbox>
          </p>
        </div>

        <div className="flex justify-between navigation-buttons step-4">
          <button className="back-btn" onClick={() => props.setStep(3)}>
            {t('back')}
          </button>
          <button
            className={`px-4 py-2 btn btn-primary ${loading ? "btn-loading disabled" : ""}`}
            disabled={!acceptedTerms || loading}
            onClick={async () => {
              setLoading(true);

              try {
                props.schedulingId
                  ? await rescheduleAnAppointment(props.appointmentForm!, props.schedulingId, AppointmentCreatedByEnum.MANAGER)
                  : await makeAnAppointment(props.appointmentForm!, userProfile, AppointmentCreatedByEnum.MANAGER);
                setSuccessDialogOpen(true);
                setLoading(false);
              } catch (error: any) {
                setLoading(false);
                setErrorDialog({ show: true, title: t('schedulingError'), body: error.message });
              }
            }}
          >
            {t('confirm')}
          </button>
        </div>
      </div>

      <SuccessDialog
        open={successDialogOpen}
        appointmentForm={props.appointmentForm}
      />

      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={() => setErrorDialog({ show: false, title: '', body: '' })}
      />
    </div>
  );
};

export default AppointmentSummary;
